import { Fragment } from "react";
import type { BlogBlock } from "@/data/blogPosts";

// Renders **bold** inline; keeps everything else as plain text.
const renderInline = (text: string) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={i} className="font-semibold text-primary">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
};

interface Props {
  blocks: BlogBlock[];
}

export const BlogContent = ({ blocks }: Props) => {
  return (
    <div className="space-y-6 text-primary/90 text-lg leading-relaxed font-light">
      {blocks.map((block, idx) => {
        switch (block.type) {
          case "h2":
            return (
              <h2
                key={idx}
                className="font-serif text-3xl md:text-4xl font-bold text-gold mt-12 mb-4"
              >
                {block.text}
              </h2>
            );
          case "h3":
            return (
              <h3
                key={idx}
                className="font-serif text-2xl font-bold text-gold mt-8 mb-3"
              >
                {block.text}
              </h3>
            );
          case "p":
            return <p key={idx}>{renderInline(block.text)}</p>;
          case "ul":
            return (
              <ul key={idx} className="space-y-3 pl-6 list-disc marker:text-gold">
                {block.items.map((item, i) => (
                  <li key={i}>{renderInline(item)}</li>
                ))}
              </ul>
            );
          case "ol":
            return (
              <ol key={idx} className="space-y-3 pl-6 list-decimal marker:text-gold marker:font-semibold">
                {block.items.map((item, i) => (
                  <li key={i}>{renderInline(item)}</li>
                ))}
              </ol>
            );
          case "quote":
            return (
              <blockquote
                key={idx}
                className="border-l-4 border-gold pl-6 italic text-primary/80 my-8"
              >
                {renderInline(block.text)}
              </blockquote>
            );
          default:
            return null;
        }
      })}
    </div>
  );
};
